
import { RunsheetShiftGridRow } from '@/entities/gridTables/model/runsheet/RunsheetShiftGridRow'
import { runsheetShiftTablesSelector } from '@/entities/gridTables/selectors/runsheetShiftTablesSelector'
import { setRunsheetShiftTableLoading } from '@/entities/gridTables/slices/runsheetShiftTablesSlice'
import { addBreak } from '@/entities/gridTables/thunks/addBreak'
import { addLoad } from '@/entities/gridTables/thunks/addLoad'
import { lockOrder } from '@/entities/gridTables/thunks/lockOrder'
import { processUnassignOrder } from '@/entities/gridTables/thunks/processUnassignOrders'
import { removeBreak } from '@/entities/gridTables/thunks/removeBreak'
import { updateRunsheetShiftTableState } from '@/entities/gridTables/thunks/updateRunsheetShiftTableState'
import { fetchRunsheets, runsheetShiftsSelectors, StopTypeEnum } from '@/entities/schedules'
import { RunsheetShift } from '@/entities/schedules/models/runsheet/RunsheetShift'
import { RunsheetStop } from '@/entities/schedules/models/runsheet/RunsheetStop'
import { selectRunsheetStopsByIds } from '@/entities/schedules/selectors/runsheetStopsSelectors'
import { optimiseShift } from '@/entities/schedules/thunks/optimiseShift'
import { ViewCSRDialog } from '@/fragments/OperationsDialogs/ViewCSRDialog/ViewCSRDialog'
import { EditBreakDuration } from '@/fragments/RunsheetShiftTable/EditBreakDurationDialog'
import { GridRowActions } from '@/shared/components/GridTable/models/GridRowActions'
import { useDialogContext } from '@/shared/components/Modal/DialogContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faArrowDown19, faFilePdf, faLock, faPenToSquare, faTrashUndo } from '@fortawesome/pro-regular-svg-icons'
import { faCoffee, faTruck } from '@fortawesome/pro-solid-svg-icons'
import { useTheme } from '@mui/material'
import { EntityId } from '@reduxjs/toolkit'

export type RunsheetShiftTableData = {
  runsheetShift: RunsheetShift
  rows?: RunsheetShiftGridRow[]
  stops: RunsheetStop[]
  shiftId: EntityId
  isLoading: boolean
  isReadOnly: boolean
  isSolverRunning: boolean
}

export const useRunsheetShiftTableData = (shiftId: EntityId): RunsheetShiftTableData => {
  const runsheetShift = useAppSelector(state => runsheetShiftsSelectors.selectById(state, shiftId))
  const runsheetId = runsheetShift?.runsheetId
  const stops = useAppSelector(state => selectRunsheetStopsByIds(state, runsheetShift?.stopIds))
  const runsheetShiftTable = useAppSelector(state => runsheetShiftTablesSelector.selectById(state, shiftId))
  const { openDialog } = useDialogContext()
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const setShiftLoadingState = (loadingState: boolean) => {
    // Set loading state
    dispatch(setRunsheetShiftTableLoading({ id: shiftId, isLoading: loadingState }))
  }

  const onResequence = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(optimiseShift({ shiftId, fromStopId: stopId }))
    await dispatch(fetchRunsheets({ runsheetIds: [runsheetId] }))
    await dispatch(updateRunsheetShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onAddBreak = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(addBreak({ shiftId, stopId: stopId }))
    await dispatch(fetchRunsheets({ runsheetIds: [runsheetId] }))
    await dispatch(updateRunsheetShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onRemoveBreak = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(removeBreak({ shiftId, stopId: stopId }))
    await dispatch(fetchRunsheets({ runsheetIds: [runsheetId] }))
    await dispatch(updateRunsheetShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onEditBreakDuration = async (stopId: EntityId, breakDuration: string) => {
    setShiftLoadingState(true)

    // Open dialog to edit break duration
    openDialog(EditBreakDuration, {
      breakDuration: breakDuration,
      stopId,
      shiftId,
      runsheetId,
      onClose: () => {
        setShiftLoadingState(false)
      },
    })
  }

  const onAddLoad = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(addLoad({ stopId: stopId }))
    await dispatch(fetchRunsheets({ runsheetIds: [runsheetId] }))
    await dispatch(updateRunsheetShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onUnassignOrder = async (orderId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(processUnassignOrder({ shiftId, orderIds: [orderId] }))
    await dispatch(updateRunsheetShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onLockOrder = async (orderId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(lockOrder({ shiftId, orderIds: [orderId] }))
    await dispatch(updateRunsheetShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onViewCSR = async (orderId: EntityId, orderNumber: string) => {
    openDialog(ViewCSRDialog, {
      title: `Customer Service Report for Order ${orderNumber}`,
      orderId: orderId,
    })
  }

  const rows = (runsheetShiftTable?.rows || []).map(row => {
    let rowActions: GridRowActions = []

    if (row.stopType === StopTypeEnum.Order) {
      rowActions = [
        { type: 'action', label: 'Resequence from here', icon: faArrowDown19, onClick: () => onResequence(row.id) },
        { type: 'action', label: 'Add Break', icon: faCoffee, onClick: () => onAddBreak(row.id) },
        { type: 'action', label: 'Add Load', icon: faTruck, onClick: () => onAddLoad(row.id) },
        { type: 'divider' },
        { type: 'action', label: 'Unassign Order', icon: faTrashUndo, onClick: () => onUnassignOrder(row.orderId) },
        { type: 'action', label: 'Lock Order', icon: faLock, onClick: () => onLockOrder(row.orderId) },
        { type: 'divider' },
        { type: 'action', label: 'View CSR', icon: faFilePdf, onClick: () => onViewCSR(row.orderId, row.orderNumber) },
        
      ]
    } else if (row.stopType === StopTypeEnum.Break) {
      rowActions = [
        { type: 'action', label: 'Resequence from here', icon: faArrowDown19, onClick: () => onResequence(row.id) },
        { type: 'action', label: 'Remove Break', icon: faCoffee, uiColor: theme.palette.error.main, onClick: () => onRemoveBreak(row.id) },
        { type: 'action', label: 'Edit Break Duration', icon: faPenToSquare, onClick: () => onEditBreakDuration(row.id, row.timeAtStop) },
      ]
    }

    return {
      ...row,
      rowActions,
    }
  })

  return {
    runsheetShift,
    isLoading: runsheetShiftTable?.isLoading || false,
    rows,
    stops,
    shiftId,
    isReadOnly: false,
    isSolverRunning: false,
  }
}